import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseToolOutput, formatEmailTime } from "./shared/utils";

interface DocFile {
  id: string;
  name?: string;
  title?: string;
  modifiedTime?: string;
  webViewLink?: string;
  url?: string;
}

interface ParsedDocsList {
  ok: true;
  files?: DocFile[];
  documents?: DocFile[];
}

interface DocsListCardProps {
  part: ToolUIPart;
}

export const DocsListCard = memo(function DocsListCard({ part }: DocsListCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedDocsList>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const docs = result.kind === "ok"
    ? (result.data.files ?? result.data.documents ?? [])
    : [];

  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : `${docs.length} doc${docs.length !== 1 ? "s" : ""}`;

  return (
    <CardShell
      icon="◻"
      title="Documents"
      badge={badge}
      accent={isError ? "error" : "docs"}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Loading documents...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.kind === "error" ? result.error : "Error"}</div>
      )}
      {!isStreaming && !isError && docs.length === 0 && (
        <div className="tool-card__empty">No documents found.</div>
      )}
      {!isStreaming && !isError && docs.length > 0 && (
        <div role="list">
          {docs.map((doc, i) => {
            const name = doc.name ?? doc.title ?? "(untitled)";
            const link = doc.webViewLink ?? doc.url;
            const modified = doc.modifiedTime ? formatEmailTime(doc.modifiedTime) : "";

            return (
              <div key={doc.id ?? i} className="tool-card__doc-row" role="listitem">
                <span className="tool-card__doc-icon tool-card__doc-icon--docs" aria-hidden="true">◻</span>
                <span className="tool-card__doc-details">
                  {link ? (
                    <a
                      className="tool-card__doc-name"
                      href={link}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={`${name} (opens in new tab)`}
                    >
                      {name}
                    </a>
                  ) : (
                    <span className="tool-card__doc-name">{name}</span>
                  )}
                </span>
                {modified && (
                  <span className="tool-card__doc-time">{modified}</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </CardShell>
  );
});
